import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Calendar, CheckCircle, Clock, AlertTriangle, 
  Plus, ChevronRight, Folder, Upload, Link, Eye, Trash2,
  Edit2, Check, X, Target, Bell, Download, ExternalLink,
  GraduationCap, DollarSign, Users, Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status colors
const STATUS_COLORS = {
  not_started: { bg: 'bg-slate-500/20', text: 'text-slate-300', label: 'Not Started' },
  in_progress: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Progress' },
  submitted: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Submitted' },
  received: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Received' },
  action_required: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Action Required' },
  complete: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Complete' }
};

// Deadline type icons
const DEADLINE_ICONS = {
  fafsa: DollarSign,
  css_profile: DollarSign,
  school_application: GraduationCap,
  early_decision: Target,
  early_action: Target,
  regular_decision: FileText,
  scholarship: Award,
  deposit: DollarSign,
  housing: Users,
  orientation: Calendar,
  enrollment: GraduationCap,
  custom: Calendar
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Days until
const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  const deadline = new Date(dateStr);
  const diff = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  return diff;
};

// Application Card
const ApplicationCard = ({ application, onUpdate, onViewDetails }) => {
  const status = STATUS_COLORS[application.overall_status] || STATUS_COLORS.not_started;
  const daysUntil = getDaysUntil(application.application_deadline);
  
  return (
    <GlassCard className="p-5 hover:border-slate-600 transition-all cursor-pointer" onClick={onViewDetails}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold text-lg">{application.institution_name}</h3>
          <p className="text-slate-300 text-sm capitalize">{application.application_type} Decision</p>
        </div>
        <Badge className={`${status.bg} ${status.text} border-none`}>
          {status.label}
        </Badge>
      </div>
      
      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-slate-300">Completion</span>
          <span className="text-white font-medium">{application.completion_percent}%</span>
        </div>
        <Progress value={application.completion_percent} className="h-2" />
      </div>
      
      {/* Components Summary */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {application.components?.slice(0, 6).map((comp) => (
          <div 
            key={comp.id}
            className={`p-2 rounded text-center ${
              comp.status === 'complete' ? 'bg-emerald-500/10' :
              comp.status === 'in_progress' ? 'bg-blue-500/10' :
              'bg-slate-800/50'
            }`}
          >
            <p className={`text-xs truncate ${
              comp.status === 'complete' ? 'text-emerald-400' :
              comp.status === 'in_progress' ? 'text-blue-400' :
              'text-slate-300'
            }`}>
              {comp.name?.split(' ')[0]}
            </p>
          </div>
        ))}
      </div>
      
      {/* Deadline */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-700">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-slate-300" />
          <span className="text-slate-300">Deadline: {formatDate(application.application_deadline)}</span>
        </div>
        {daysUntil !== null && (
          <span className={`text-sm font-medium ${
            daysUntil < 0 ? 'text-red-400' :
            daysUntil <= 7 ? 'text-amber-400' :
            daysUntil <= 30 ? 'text-blue-400' :
            'text-slate-300'
          }`}>
            {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` :
             daysUntil === 0 ? 'Due today' :
             `${daysUntil}d left`}
          </span>
        )}
      </div>
    </GlassCard>
  );
};

// Deadline Card
const DeadlineCard = ({ deadline, onComplete }) => {
  const Icon = DEADLINE_ICONS[deadline.deadline_type] || Calendar;
  const daysUntil = deadline.days_until ?? getDaysUntil(deadline.due_date);
  
  return (
    <div className={`p-4 rounded-xl border ${
      deadline.is_completed ? 'bg-emerald-500/5 border-emerald-500/20' :
      deadline.is_overdue ? 'bg-red-500/5 border-red-500/20' :
      daysUntil <= 7 ? 'bg-amber-500/5 border-amber-500/20' :
      'bg-slate-800/50 border-slate-700'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            deadline.is_completed ? 'bg-emerald-500/20' :
            deadline.is_critical ? 'bg-red-500/20' : 'bg-slate-700'
          }`}>
            {deadline.is_completed ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <Icon className={`w-5 h-5 ${deadline.is_critical ? 'text-red-400' : 'text-slate-300'}`} />
            )}
          </div>
          <div>
            <p className={`font-medium ${deadline.is_completed ? 'text-emerald-400 line-through' : 'text-white'}`}>
              {deadline.title}
            </p>
            {deadline.institution_name && (
              <p className="text-slate-300 text-sm">{deadline.institution_name}</p>
            )}
            {deadline.description && (
              <p className="text-slate-300 text-xs mt-1">{deadline.description}</p>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <p className={`text-sm font-medium ${
            deadline.is_completed ? 'text-emerald-400' :
            deadline.is_overdue ? 'text-red-400' :
            daysUntil <= 7 ? 'text-amber-400' :
            'text-slate-300'
          }`}>
            {formatDate(deadline.due_date)}
          </p>
          {!deadline.is_completed && daysUntil !== null && (
            <p className={`text-xs ${
              deadline.is_overdue ? 'text-red-400' :
              daysUntil <= 7 ? 'text-amber-400' :
              'text-slate-300'
            }`}>
              {deadline.is_overdue ? `${Math.abs(daysUntil)} days overdue` :
               daysUntil === 0 ? 'Due today!' :
               `${daysUntil} days left`}
            </p>
          )}
        </div>
      </div>
      
      {!deadline.is_completed && (
        <div className="flex justify-end mt-3">
          <Button size="sm" variant="ghost" onClick={() => onComplete(deadline.id)} className="text-emerald-400">
            <Check className="w-4 h-4 mr-1" /> Mark Complete
          </Button>
        </div>
      )}
    </div>
  );
};

// Checklist Item
const ChecklistItemCard = ({ item, onToggle }) => {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${
      item.is_completed ? 'bg-emerald-500/5' : 'bg-slate-800/30'
    }`}>
      <button
        onClick={() => onToggle(item.id, !item.is_completed)}
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          item.is_completed 
            ? 'border-emerald-500 bg-emerald-500' 
            : 'border-slate-600 hover:border-blue-500'
        }`}
      >
        {item.is_completed && <Check className="w-4 h-4 text-white" />}
      </button>
      <div className="flex-1">
        <p className={`font-medium ${item.is_completed ? 'text-emerald-400 line-through' : 'text-white'}`}>
          {item.title}
        </p>
        {item.description && (
          <p className="text-slate-300 text-sm mt-0.5">{item.description}</p>
        )}
        {item.due_date && (
          <p className="text-slate-300 text-xs mt-1">Due: {formatDate(item.due_date)}</p>
        )}
      </div>
      <Badge className={`bg-slate-700 text-slate-300 border-none text-xs`}>
        {item.category}
      </Badge>
    </div>
  );
};

// Document Card
const DocumentCard = ({ document, onDelete }) => {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
          <FileText className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <p className="text-white font-medium">{document.name}</p>
          <p className="text-slate-300 text-xs capitalize">{document.document_type?.replace('_', ' ')}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {document.file_url && (
          <Button size="sm" variant="ghost" asChild>
            <a href={document.file_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(document.id)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Add Application Modal
const AddApplicationModal = ({ isOpen, onClose, onSubmit, paths }) => {
  const [formData, setFormData] = useState({
    institution_name: '',
    path_id: '',
    application_type: 'regular',
    application_deadline: '',
    application_fee: 0
  });
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-[#0a1729]/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-800">
          <h2 className="text-xl font-bold text-white">Add Application</h2>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="text-white text-sm mb-2 block">Institution Name</label>
            <input
              type="text"
              value={formData.institution_name}
              onChange={(e) => setFormData(prev => ({ ...prev, institution_name: e.target.value }))}
              className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
              placeholder="e.g., Duke University"
            />
          </div>
          
          {paths?.length > 0 && (
            <div>
              <label className="text-white text-sm mb-2 block">Link to Path (Optional)</label>
              <select
                value={formData.path_id}
                onChange={(e) => setFormData(prev => ({ ...prev, path_id: e.target.value }))}
                className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
              >
                <option value="">Select a path...</option>
                {paths.map(p => (
                  <option key={p.id} value={p.id}>{p.institution_name}</option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="text-white text-sm mb-2 block">Application Type</label>
            <div className="grid grid-cols-2 gap-2">
              {['early_decision', 'early_action', 'regular', 'rolling'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFormData(prev => ({ ...prev, application_type: type }))}
                  className={`p-3 rounded-lg border text-sm capitalize transition-colors ${
                    formData.application_type === type
                      ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  {type.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="text-white text-sm mb-2 block">Deadline</label>
            <input
              type="date"
              value={formData.application_deadline}
              onChange={(e) => setFormData(prev => ({ ...prev, application_deadline: e.target.value }))}
              className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
          
          <div>
            <label className="text-white text-sm mb-2 block">Application Fee ($)</label>
            <input
              type="number"
              value={formData.application_fee}
              onChange={(e) => setFormData(prev => ({ ...prev, application_fee: parseFloat(e.target.value) || 0 }))}
              className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="border-slate-600">Cancel</Button>
          <Button 
            onClick={() => { onSubmit(formData); onClose(); }}
            disabled={!formData.institution_name}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Create Application
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Main Component
export default function ApplicationPipeline() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [deadlines, setDeadlines] = useState({ urgent: [], upcoming: [], later: [] });
  const [checklist, setChecklist] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [paths, setPaths] = useState([]);
  const [showAddApp, setShowAddApp] = useState(false);
  
  const studentId = user?.id;
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, deadlinesRes, pathsRes, docsRes] = await Promise.all([
        axios.get(`${API}/applications/${studentId}`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/deadlines/${studentId}`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/paths/my-paths`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/documents/${studentId}`, { ...getAuthHeader(), withCredentials: true })
      ]);
      
      setApplications(appsRes.data.applications || []);
      setDeadlines(deadlinesRes.data);
      setPaths(pathsRes.data.paths || []);
      setDocuments(docsRes.data.documents || []);
      
      // Fetch checklist for first path if exists
      if (pathsRes.data.paths?.length > 0) {
        const checklistRes = await axios.get(
          `${API}/checklists/${studentId}/${pathsRes.data.paths[0].id}`,
          { ...getAuthHeader(), withCredentials: true }
        );
        setChecklist(checklistRes.data.checklist);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateApplication = async (appData) => {
    try {
      await axios.post(`${API}/applications`, appData, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Application created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create application');
    }
  };
  
  const handleCompleteDeadline = async (deadlineId) => {
    try {
      await axios.put(`${API}/deadlines/${deadlineId}/complete`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Deadline marked as complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to update deadline');
    }
  };
  
  const handleToggleChecklistItem = async (itemId, isCompleted) => {
    if (!checklist) return;
    
    try {
      await axios.put(`${API}/checklists/${checklist.id}/item/${itemId}`, {
        is_completed: isCompleted
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      
      // Update local state
      setChecklist(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.id === itemId ? { ...item, is_completed: isCompleted } : item
        ),
        completed_items: prev.completed_items + (isCompleted ? 1 : -1),
        completion_percent: Math.round(((prev.completed_items + (isCompleted ? 1 : -1)) / prev.total_items) * 100)
      }));
      
      toast.success(isCompleted ? 'Item completed!' : 'Item unchecked');
    } catch (error) {
      toast.error('Failed to update checklist');
    }
  };
  
  const handleGenerateChecklist = async () => {
    if (!paths.length) {
      toast.error('No paths selected');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/checklists/generate/${paths[0].id}`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setChecklist(res.data.checklist);
      toast.success('Checklist generated!');
    } catch (error) {
      toast.error('Failed to generate checklist');
    }
  };
  
  const handleGenerateDeadlines = async () => {
    if (!paths.length) {
      toast.error('No paths selected');
      return;
    }
    
    try {
      await axios.post(`${API}/deadlines/generate/${paths[0].id}`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      fetchData();
      toast.success('Deadlines generated!');
    } catch (error) {
      toast.error('Failed to generate deadlines');
    }
  };
  
  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;
    
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }
  
  const totalDeadlines = (deadlines.urgent?.length || 0) + (deadlines.upcoming?.length || 0) + (deadlines.later?.length || 0);
  
  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Application Pipeline</h1>
            <p className="text-slate-300 mt-1">Track applications, deadlines, and documents</p>
          </div>
          <Button onClick={() => setShowAddApp(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Add Application
          </Button>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{applications.length}</p>
                <p className="text-slate-300 text-sm">Applications</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{deadlines.urgent?.length || 0}</p>
                <p className="text-slate-300 text-sm">Urgent Deadlines</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {checklist ? `${checklist.completion_percent}%` : '0%'}
                </p>
                <p className="text-slate-300 text-sm">Checklist Done</p>
              </div>
            </div>
          </GlassCard>
          
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Folder className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{documents.length}</p>
                <p className="text-slate-300 text-sm">Documents</p>
              </div>
            </div>
          </GlassCard>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-slate-800/50 p-1 mb-6 grid grid-cols-4">
            <TabsTrigger value="applications" className="data-[state=active]:bg-blue-600">
              <FileText className="w-4 h-4 mr-2" /> Applications
            </TabsTrigger>
            <TabsTrigger value="deadlines" className="data-[state=active]:bg-blue-600">
              <Calendar className="w-4 h-4 mr-2" /> Deadlines
              {deadlines.urgent?.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{deadlines.urgent.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="checklist" className="data-[state=active]:bg-blue-600">
              <CheckCircle className="w-4 h-4 mr-2" /> Checklist
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-blue-600">
              <Folder className="w-4 h-4 mr-2" /> Documents
            </TabsTrigger>
          </TabsList>
          
          {/* Applications Tab */}
          <TabsContent value="applications">
            {applications.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {applications.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    application={app}
                    onViewDetails={() => toast.info(`Viewing ${app.institution_name}`)}
                  />
                ))}
              </div>
            ) : (
              <GlassCard className="p-12 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Applications Yet</h3>
                <p className="text-slate-300 mb-6">Start tracking your college applications</p>
                <Button onClick={() => setShowAddApp(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add First Application
                </Button>
              </GlassCard>
            )}
          </TabsContent>
          
          {/* Deadlines Tab */}
          <TabsContent value="deadlines">
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={handleGenerateDeadlines} variant="outline" className="border-slate-600">
                  <Plus className="w-4 h-4 mr-2" /> Generate Standard Deadlines
                </Button>
              </div>
              
              {/* Urgent */}
              {deadlines.urgent?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Urgent (Next 7 Days)
                  </h3>
                  <div className="space-y-3">
                    {deadlines.urgent.map((d) => (
                      <DeadlineCard key={d.id} deadline={d} onComplete={handleCompleteDeadline} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upcoming */}
              {deadlines.upcoming?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" /> Upcoming (Next 30 Days)
                  </h3>
                  <div className="space-y-3">
                    {deadlines.upcoming.map((d) => (
                      <DeadlineCard key={d.id} deadline={d} onComplete={handleCompleteDeadline} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Later */}
              {deadlines.later?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" /> Later
                  </h3>
                  <div className="space-y-3">
                    {deadlines.later.map((d) => (
                      <DeadlineCard key={d.id} deadline={d} onComplete={handleCompleteDeadline} />
                    ))}
                  </div>
                </div>
              )}
              
              {totalDeadlines === 0 && (
                <GlassCard className="p-12 text-center">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Deadlines</h3>
                  <p className="text-slate-300 mb-6">Generate deadlines based on your selected paths</p>
                  <Button onClick={handleGenerateDeadlines} className="bg-blue-600 hover:bg-blue-700">
                    Generate Deadlines
                  </Button>
                </GlassCard>
              )}
            </div>
          </TabsContent>
          
          {/* Checklist Tab */}
          <TabsContent value="checklist">
            {checklist ? (
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Application Checklist</h3>
                    <p className="text-slate-300 text-sm">
                      {checklist.completed_items} of {checklist.total_items} items complete
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">{checklist.completion_percent}%</p>
                    <Progress value={checklist.completion_percent} className="w-32 h-2 mt-1" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  {checklist.items?.map((item) => (
                    <ChecklistItemCard
                      key={item.id}
                      item={item}
                      onToggle={handleToggleChecklistItem}
                    />
                  ))}
                </div>
              </GlassCard>
            ) : (
              <GlassCard className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Checklist Generated</h3>
                <p className="text-slate-300 mb-6">Generate a checklist based on your selected path</p>
                <Button onClick={handleGenerateChecklist} className="bg-blue-600 hover:bg-blue-700">
                  Generate Checklist
                </Button>
              </GlassCard>
            )}
          </TabsContent>
          
          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="space-y-4">
              {documents.length > 0 ? (
                documents.map((doc) => (
                  <DocumentCard key={doc.id} document={doc} onDelete={handleDeleteDocument} />
                ))
              ) : (
                <GlassCard className="p-12 text-center">
                  <Folder className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Document Vault Empty</h3>
                  <p className="text-slate-300 mb-6">Store important documents like transcripts, essays, and acceptance letters</p>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Upload className="w-4 h-4 mr-2" /> Upload Document
                  </Button>
                </GlassCard>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Add Application Modal */}
        <AddApplicationModal
          isOpen={showAddApp}
          onClose={() => setShowAddApp(false)}
          onSubmit={handleCreateApplication}
          paths={paths}
        />
      </div>
    </AppLayout>
  );
}
